import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import { Severity, valuesExist } from "../scenarioValidator";
import type { CorrelationValidationGenerator } from "./correlationChecks";

export const correlationsAtPumpIntakeChecks: CorrelationValidationGenerator = (
  correlationLimits,
  correlationField,
  { calcs }
) => {
  const intakeStageCalculations =
    calcs.pumpCalculations.individual.value[
      calcs.ascendingOrderPumps.value[0]?.id
    ]?.intakeStageCalculations;

  if (!intakeStageCalculations) return [];

  const intakeFluidProperties =
    intakeStageCalculations.dischargeFluidPropertiesContext.fluidProperties;

  return [
    {
      name: `${correlationField}_oil_gravity_pump_intake`,
      message: `API Out of Range @ Pump Intake`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.PUMP,
      isActive: valuesExist([
        correlationLimits.MIN_API,
        correlationLimits.MAX_API,
        calcs.basicData.value.oilAPI
      ]),
      isInvalid:
        calcs.basicData.value.oilAPI > correlationLimits.MAX_API ||
        calcs.basicData.value.oilAPI < correlationLimits.MIN_API
    },
    {
      name: `${correlationField}_gas_specific_gravity_pump_intake`,
      message: `Gas Specific Gravity Out of Range @ Pump Intake`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.PUMP,
      isActive: valuesExist([
        correlationLimits.MIN_GAS_SG,
        correlationLimits.MAX_GAS_SG,
        calcs.basicData.value.gasSpecificGravity
      ]),
      isInvalid:
        calcs.basicData.value.gasSpecificGravity >
          correlationLimits.MAX_GAS_SG ||
        calcs.basicData.value.gasSpecificGravity < correlationLimits.MIN_GAS_SG
    },
    {
      name: `${correlationField}_temperature_pump_intake`,
      message: `Temperature Out of Range @ Pump Intake`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.PUMP,
      isActive: valuesExist([
        correlationLimits.MIN_TEMP,
        correlationLimits.MAX_TEMP,
        intakeStageCalculations.intakePressurePSIA
      ]),
      isInvalid:
        intakeStageCalculations.intakePressurePSIA >
          correlationLimits.MAX_TEMP ||
        intakeStageCalculations.intakePressurePSIA < correlationLimits.MIN_TEMP
    },
    {
      name: `${correlationField}_pressure_pump_intake`,
      message: `Pressure Out of Range @ Pump Intake`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.PUMP,
      isActive: valuesExist([
        correlationLimits.MIN_PRESS,
        correlationLimits.MAX_PRESS,
        intakeStageCalculations.intakePressurePSIA
      ]),
      isInvalid:
        intakeStageCalculations.intakePressurePSIA >
          correlationLimits.MAX_PRESS ||
        intakeStageCalculations.intakePressurePSIA < correlationLimits.MIN_PRESS
    },
    {
      name: `${correlationField}_oil_solution_gas_pump_intake`,
      message: `Oil Solution Gas Out of Range @ Pump Intake`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.PUMP,
      isActive: valuesExist([
        correlationLimits.MIN_RSO,
        correlationLimits.MAX_RSO,
        intakeFluidProperties.oilPhaseGasSolubilitySCFSTB
      ]),
      isInvalid:
        intakeFluidProperties.oilPhaseGasSolubilitySCFSTB >
          correlationLimits.MAX_RSO ||
        intakeFluidProperties.oilPhaseGasSolubilitySCFSTB <
          correlationLimits.MIN_RSO
    },
    {
      name: `${correlationField}_bubblepoint_pressure_pump_intake`,
      message: `Bubblepoint Pressure Out of Range @ Pump Intake`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.PUMP,
      isActive: valuesExist([
        correlationLimits.MIN_PB,
        correlationLimits.MAX_PB,
        intakeFluidProperties.bubblePointPressurePSIA
      ]),
      isInvalid:
        intakeFluidProperties.bubblePointPressurePSIA >
          correlationLimits.MAX_PB ||
        intakeFluidProperties.bubblePointPressurePSIA < correlationLimits.MIN_PB
    },
    {
      name: `${correlationField}_oil_formation_volume_factor_pump_intake`,
      message: `Oil Formation Volume Factor Out of Range @ Pump Intake`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.PUMP,
      isActive: valuesExist([
        correlationLimits.MIN_BO,
        correlationLimits.MAX_BO,
        intakeFluidProperties.oilVolumeFactorBBLSTB
      ]),
      isInvalid:
        intakeFluidProperties.oilVolumeFactorBBLSTB >
          correlationLimits.MAX_BO ||
        intakeFluidProperties.oilVolumeFactorBBLSTB < correlationLimits.MIN_BO
    },
    {
      name: `${correlationField}_dead_oil_viscosity_pump_intake`,
      message: `Dead Oil Viscosity Out of Range @ Pump Intake`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.PUMP,
      isActive: valuesExist([
        correlationLimits.MIN_U_OD,
        correlationLimits.MAX_U_OD,
        intakeFluidProperties.deadOilViscosityCP
      ]),
      isInvalid:
        intakeFluidProperties.deadOilViscosityCP > correlationLimits.MAX_U_OD ||
        intakeFluidProperties.deadOilViscosityCP < correlationLimits.MIN_U_OD
    },
    {
      name: `${correlationField}_live_oil_viscosity_pump_intake`,
      message: `Live Oil Viscosity Out of Range @ Pump Intake`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.PUMP,
      isActive: valuesExist([
        correlationLimits.MIN_U_O,
        correlationLimits.MAX_U_O,
        intakeFluidProperties.deadOilViscosityCP
      ]),
      isInvalid:
        intakeFluidProperties.deadOilViscosityCP > correlationLimits.MAX_U_O ||
        intakeFluidProperties.deadOilViscosityCP < correlationLimits.MIN_U_O
    },
    {
      name: `${correlationField}_pseudo_reduced_pressure_pump_intake`,
      message: `Pseudo-Reduced Pressure Out of Range @ Pump Intake`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.PUMP,
      isActive: valuesExist([
        correlationLimits.MIN_PPR,
        correlationLimits.MAX_PPR,
        intakeFluidProperties.psuedoCriticals.psuedoReducedPressure
      ]),
      isInvalid:
        intakeFluidProperties.psuedoCriticals.psuedoReducedPressure >
          correlationLimits.MAX_PPR ||
        intakeFluidProperties.psuedoCriticals.psuedoReducedPressure <
          correlationLimits.MIN_PPR
    },
    {
      name: `${correlationField}_pseudo_reduced_temperature_pump_intake`,
      message: `Pseudo-Reduced Temperature Out of Range @ Pump Intake`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.PUMP,
      isActive: valuesExist([
        correlationLimits.MIN_TPR,
        correlationLimits.MAX_TPR,
        intakeFluidProperties.psuedoCriticals.psuedoReducedTemperature
      ]),
      isInvalid:
        intakeFluidProperties.psuedoCriticals.psuedoReducedTemperature >
          correlationLimits.MAX_TPR ||
        intakeFluidProperties.psuedoCriticals.psuedoReducedTemperature <
          correlationLimits.MIN_TPR
    }
  ];
};
